import React from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';

import Input from '../../ui/Input';

// "Site" entries use the same CSS custom properties as the rest of the app
// (see tokens.css) so a label picked here always matches the site's actual
// heading/body font, even if tokens.css is retuned later.
const FONT_FAMILIES = [
  { label: 'Site Title Font', value: 'var(--ss-font-heading)' },
  { label: 'Site Text Font', value: 'var(--ss-font-body)' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS' },
  { label: 'Impact', value: 'Impact' },
];

// Type-specific properties for a selected text block. Generic position/size
// live in PositionPanel/SizePanel — this panel only ever renders when a
// text block is selected (see registry.js).
export default function TextPropertiesPanel({ block, onUpdateProperty, onDelete }) {
  if (!block) return null;
  const collapsed = block.collapsed;

  // Site h1-h6 are bold by default (see base.css) — default the weight to
  // match when the site's own heading font is picked here.
  const handleFontFamilyChange = (value) => {
    onUpdateProperty('fontFamily', value);
    if (value === 'var(--ss-font-heading)') {
      onUpdateProperty('fontWeight', 'bold');
    }
  };

  return (
    <div className="ss-print__panel">
      <div className="ss-print__panel-header">
        <h3 className="ss-print__panel-title">Text Properties</h3>
        <div className="ss-print__panel-header-actions">
          <button
            type="button"
            className="ss-print__lock-toggle ss-print__lock-toggle--danger"
            onClick={onDelete}
            aria-label="Delete this text block"
            title="Delete"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="ss-print__lock-toggle"
            onClick={() => onUpdateProperty('collapsed', !collapsed)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand text properties panel' : 'Collapse text properties panel'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="ss-print__panel-row">
            <Input
              label="Font size"
              type="number"
              min={8}
              max={144}
              value={block.fontSize}
              onChange={(e) => onUpdateProperty('fontSize', Number(e.target.value))}
            />
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-text-color">
                Color
              </label>
              <input
                id="ss-print-text-color"
                type="color"
                className="ss-input"
                value={block.color}
                onChange={(e) => onUpdateProperty('color', e.target.value)}
              />
            </div>
          </div>
          <div className="ss-field">
            <label className="ss-label" htmlFor="ss-print-text-font">
              Font family
            </label>
            <select
              id="ss-print-text-font"
              className="ss-input"
              value={block.fontFamily}
              onChange={(e) => handleFontFamilyChange(e.target.value)}
            >
              {FONT_FAMILIES.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ss-print__panel-row">
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-text-weight">
                Weight
              </label>
              <select
                id="ss-print-text-weight"
                className="ss-input"
                value={block.fontWeight}
                onChange={(e) => onUpdateProperty('fontWeight', e.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
            </div>
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-text-style">
                Style
              </label>
              <select
                id="ss-print-text-style"
                className="ss-input"
                value={block.fontStyle}
                onChange={(e) => onUpdateProperty('fontStyle', e.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </div>
          </div>
          <div className="ss-field">
            <label className="ss-label" htmlFor="ss-print-text-align">
              Text align
            </label>
            <select
              id="ss-print-text-align"
              className="ss-input"
              value={block.textAlign}
              onChange={(e) => onUpdateProperty('textAlign', e.target.value)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
}
